import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../lib/app-context';
import type { AppRoute } from '../types';
import { Brand } from './Brand';
import { Icon } from './Icon';
import { signOut } from '../lib/supabase';

type NavItem = {
  route: AppRoute;
  icon: React.ComponentProps<typeof Icon>['name'];
  label: string;
  aliases?: string[];
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const navGroups: NavGroup[] = [
  {
    label: 'BUGUN',
    items: [
      { route: 'dashboard', icon: 'home', label: 'Bosh sahifa', aliases: ['bugun', 'dashboard', 'asosiy'] },
      { route: 'lesson', icon: 'book', label: 'Bugungi dars', aliases: ['dars', 'lesson', 'mission'] },
    ],
  },
  {
    label: 'O‘RGANISH',
    items: [
      { route: 'vocabulary', icon: 'cards', label: 'So‘zlar', aliases: ['vocabulary', 'lug‘at', 'sozlar'] },
      { route: 'grammar', icon: 'grammar', label: 'Grammatika', aliases: ['grammar'] },
      { route: 'skills', icon: 'headphones', label: 'Ko‘nikmalar', aliases: ['listening', 'reading', 'speaking', 'talaffuz'] },
    ],
  },
  {
    label: 'MUSTAHKAMLASH',
    items: [
      { route: 'review', icon: 'repeat', label: 'Takrorlash', aliases: ['review', 'recovery', 'srs'] },
      { route: 'boss', icon: 'trophy', label: 'Boss Battle', aliases: ['boss', 'sinov'] },
    ],
  },
  {
    label: 'NATIJALAR',
    items: [
      { route: 'knowledge', icon: 'spark', label: 'Bilim holati', aliases: ['mastery', 'knowledge', 'bilim'] },
      { route: 'stats', icon: 'chart', label: 'Statistika', aliases: ['stats', 'natija', 'analytics'] },
      { route: 'profile', icon: 'user', label: 'Profil', aliases: ['account', 'sozlamalar', 'settings'] },
    ],
  },
];

const mobileNav: NavItem[] = [
  { route: 'dashboard', icon: 'home', label: 'Bosh' },
  { route: 'lesson', icon: 'book', label: 'Dars' },
  { route: 'review', icon: 'repeat', label: 'Takror' },
  { route: 'stats', icon: 'chart', label: 'Natija' },
  { route: 'profile', icon: 'user', label: 'Profil' },
];

const flatNav = navGroups.flatMap((group) => group.items);

export function Shell({ children }: { children: React.ReactNode }) {
  const { state, route, setRoute, theme, setThemeMode, entitlement } = useApp();
  const telegramSurface = window.location.pathname.startsWith('/telegram');
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const activeLabel = flatNav.find((item) => item.route === route)?.label ?? 'English Forge 365';
  const planLabel = entitlement.plan === 'trial' ? 'TRIAL' : entitlement.plan.toUpperCase();
  const initial = (state.profile.name || 'E').trim().slice(0, 1).toUpperCase();

  const matchedRoute = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('uz');
    if (!q) return null;
    return flatNav.find((item) => {
      const haystack = [item.label, ...(item.aliases ?? [])].join(' ').toLocaleLowerCase('uz');
      return haystack.includes(q) || q.includes(item.label.toLocaleLowerCase('uz'));
    }) ?? null;
  }, [search]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key === 'Escape' && document.activeElement === searchRef.current) {
        searchRef.current?.blur();
        setSearch('');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);


  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [route]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;
    const selector = '.premium-content .card.glass, .premium-content .premium-metric, .premium-content .module-row, .topbar-icon-button, .topbar-profile, .theme-switch';
    const reset = (surface: HTMLElement) => {
      surface.style.setProperty('--ef-tilt-x', '0deg');
      surface.style.setProperty('--ef-tilt-y', '0deg');
      surface.style.setProperty('--ef-glint-x', '50%');
      surface.style.setProperty('--ef-glint-y', '0%');
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const node = event.target instanceof Element ? event.target.closest(selector) : null;
      if (!(node instanceof HTMLElement) || !shell.contains(node)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = node.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const px = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        const py = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
        const tiltX = (px - 0.5) * 1.35;
        const tiltY = (0.5 - py) * 1.1;
        node.style.setProperty('--ef-tilt-x', `${tiltX.toFixed(2)}deg`);
        node.style.setProperty('--ef-tilt-y', `${tiltY.toFixed(2)}deg`);
        node.style.setProperty('--ef-glint-x', `${(px * 100).toFixed(1)}%`);
        node.style.setProperty('--ef-glint-y', `${(py * 100).toFixed(1)}%`);
      });
    };

    const onPointerOut = (event: PointerEvent) => {
      const from = event.target instanceof Element ? event.target.closest(selector) : null;
      const to = event.relatedTarget instanceof Element ? event.relatedTarget.closest(selector) : null;
      if (from instanceof HTMLElement && from !== to) reset(from);
    };

    shell.addEventListener('pointermove', onPointerMove, { passive: true });
    shell.addEventListener('pointerout', onPointerOut, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      shell.removeEventListener('pointermove', onPointerMove);
      shell.removeEventListener('pointerout', onPointerOut);
    };
  }, []);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (matchedRoute) {
      setRoute(matchedRoute.route);
      setSearch('');
      searchRef.current?.blur();
    }
  };

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const motionDocument = document as Document & {
      startViewTransition?: (callback: () => void) => { finished: Promise<void> };
    };

    if (!reduceMotion && motionDocument.startViewTransition) {
      document.documentElement.dataset.themeMotion = 'true';
      const transition = motionDocument.startViewTransition(() => setThemeMode(nextTheme));
      void transition.finished.finally(() => { delete document.documentElement.dataset.themeMotion; });
      return;
    }
    setThemeMode(nextTheme);
  };

  const logout = async () => {
    try {
      await signOut();
    } finally {
      localStorage.removeItem('ef-telegram-session');
      window.location.replace('/login');
    }
  };

  return (
    <div ref={shellRef} className={`app-shell premium-shell-v32 premium-motion-v321 ${telegramSurface ? 'telegram-shell' : ''}`}>
      <aside className="sidebar premium-sidebar" aria-label="English Forge navigatsiyasi">
        <button className="sidebar-brand-panel" onClick={() => setRoute('dashboard')} aria-label="Bosh sahifaga qaytish">
          <Brand priority />
          <span className={`plan-badge plan-${entitlement.plan}`}>{planLabel}</span>
        </button>

        <nav className="sidebar-nav" aria-label="Asosiy bo‘limlar">
          {navGroups.map((group) => (
            <div className="nav-group" key={group.label}>
              <span className="nav-group-title">{group.label}</span>
              <div className="nav-group-items">
                {group.items.map((item) => (
                  <button
                    key={item.route}
                    className={`nav-item ${route === item.route ? 'active' : ''}`}
                    onClick={() => setRoute(item.route)}
                  >
                    <span className="nav-icon"><Icon name={item.icon} /></span>
                    <span className="nav-label">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="sidebar-spacer" />

        <div className="sidebar-bottom">
          <button className="nav-item pro-entry" onClick={() => setRoute('pricing')}>
            <span className="nav-icon"><Icon name="shield" /></span>
            <span className="nav-label">PRO imkoniyatlar</span>
          </button>
          <button className="sidebar-user" onClick={() => setRoute('profile')} aria-label="Profilni ochish">
            <span className="sidebar-user-avatar">{initial}</span>
            <span className="sidebar-user-copy">
              <strong>{state.profile.name || 'Learner'}</strong>
              <small>{state.profile.level} · Day {state.progress.day}</small>
            </span>
            <Icon name="arrow" size={16} />
          </button>
          <button className="sidebar-logout" onClick={() => void logout()} aria-label="Akkauntdan chiqish">
            <span className="nav-icon"><Icon name="logout" size={17} /></span>
            <span className="nav-label">Chiqish</span>
          </button>
          <div className="sidebar-motto">
            <span>Har kuni kichik qadam.</span>
            <strong>365 kunda katta o‘zgarish.</strong>
          </div>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar premium-topbar">
          <form className="topbar-search" onSubmit={submitSearch} role="search">
            <Icon name="search" size={20} />
            <input
              ref={searchRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Bo‘lim yoki mavzuni izlash..."
              aria-label="Bo‘lim yoki mavzuni izlash"
            />
            {search && matchedRoute ? <span className="search-match">{matchedRoute.label}</span> : <kbd>Ctrl + K</kbd>}
          </form>

          <div className="topbar-actions">
            <div className="topbar-live-stats" aria-label="Bugungi ko‘rsatkichlar">
              <span><Icon name="spark" size={14} />{state.progress.xp.toLocaleString()} XP</span>
              <span><Icon name="flame" size={14} />{state.progress.streak}</span>
            </div>
            <button
              className={`theme-switch ${theme === 'dark' ? 'dark' : 'light'}`}
              aria-label="Mavzuni almashtirish"
              onClick={toggleTheme}
            >
              <span><Icon name="sun" size={17} /></span>
              <span><Icon name="moon" size={17} /></span>
            </button>
            <button className="topbar-icon-button" aria-label="Bildirishnomalar">
              <Icon name="bell" size={19} />
            </button>
            <button className="topbar-profile" onClick={() => setRoute('profile')}>
              <span className="topbar-avatar">{initial}</span>
              <span className="topbar-profile-copy"><strong>{state.profile.name || 'Learner'}</strong><small>{activeLabel}</small></span>
              <Icon name="arrow" size={15} />
            </button>
          </div>
        </header>

        <main className="content premium-content"><div key={route} className="route-motion-stage">{children}</div></main>
      </div>

      <nav className="bottom-nav premium-bottom-nav" aria-label="Mobil navigatsiya">
        {mobileNav.map((item) => (
          <button
            key={item.route}
            className={route === item.route ? 'active' : ''}
            onClick={() => setRoute(item.route)}
          >
            <Icon name={item.icon} size={20} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
